import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import type { TaskItem as TaskItemT } from "@edge/shared";
import type { PersonalTaskInput } from "../../hooks/usePersonalTasks";
import type { CompletedTasksState } from "../../hooks/useCompletedTasks";
import type { TodoistProjectLite } from "../../api";
import { Panel } from "../shared/Panel";
import { WriteConfirm } from "../shared/WriteConfirm";
import {
  CrmTaskEditDialog,
  type CrmTaskEditDialogProps,
} from "../shared/CrmTaskEditDialog";

interface TasksRailProps {
  crmTasks: TaskItemT[];
  personalTasks: TaskItemT[];
  /** Personal-only — local D1. */
  onCompletePersonal?: (task: TaskItemT) => void;
  onSnoozePersonal?: (task: TaskItemT) => void;
  onCreatePersonal?: (input: PersonalTaskInput) => void;
  onUpdatePersonal?: (id: string, patch: PersonalTaskInput) => void;
  /** Quick reschedule — sets due_date without opening the full edit form. */
  onReschedulePersonal?: (id: string, dueDate: string) => void;
  /** Personal completed-history (lazy-loaded). */
  completedPersonal?: CompletedTasksState;
  /** Mutation counter from personal tasks — used to auto-refresh history. */
  personalVersion?: number;
  /** Todoist projects for the create dropdown (My List). */
  projects?: TodoistProjectLite[];
  /** Create a new Todoist project; returns it (or null on failure). */
  onCreateProject?: (name: string) => Promise<TodoistProjectLite | null>;
  /** CRM-only — writes back to Zoho. The caller must show write-confirm. */
  onCompleteCrm?: (task: TaskItemT) => Promise<void>;
  onRescheduleCrm?: (task: TaskItemT, newDate: string) => Promise<void>;
  /** QW-2: full edit (subject/due/priority/status/description). When present,
   *  CRM rows show a ✎ that opens the edit dialog + before→after gate. */
  onEditCrm?: CrmTaskEditDialogProps["onSave"];
  /** Deal/focus view: show ONLY this deal's CRM tasks — hides the MY LIST + ALL
   *  tabs and the personal list entirely (QW-4). */
  crmOnly?: boolean;
}

const URGENCY_RANK = { overdue: 0, today: 1, normal: 2 } as const;

/** Merge CRM + personal into one list, most-urgent first (overdue → today →
 *  normal, then by due date). Source stays visible via per-row badges. */
function sortByUrgency(tasks: TaskItemT[]): TaskItemT[] {
  return [...tasks].sort((a, b) => {
    const ru = URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency];
    if (ru !== 0) return ru;
    return (a.dueDate ?? "9999-12-31").localeCompare(b.dueDate ?? "9999-12-31");
  });
}

type EditState = { mode: "new" } | { mode: "edit"; id: string } | null;
type PendingCrm =
  | { kind: "complete"; task: TaskItemT }
  | { kind: "reschedule"; task: TaskItemT; date: string }
  | { kind: "edit"; task: TaskItemT }
  | null;

export function TasksRail({
  crmTasks,
  personalTasks,
  onCompletePersonal,
  onSnoozePersonal,
  onCreatePersonal,
  onUpdatePersonal,
  onReschedulePersonal,
  completedPersonal,
  personalVersion,
  projects,
  onCreateProject,
  onCompleteCrm,
  onRescheduleCrm,
  onEditCrm,
  crmOnly,
}: TasksRailProps) {
  const [tab, setTab] = useState<"crm" | "personal" | "all">("crm");
  const [edit, setEdit] = useState<EditState>(null);
  const [pendingCrm, setPendingCrm] = useState<PendingCrm>(null);
  // QW-3: which task's description is expanded (one at a time).
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const allTasks = useMemo(
    () => sortByUrgency([...crmTasks, ...personalTasks]),
    [crmTasks, personalTasks],
  );
  // In crmOnly (deal/focus) view the tab is pinned to CRM and the strip is hidden.
  const activeTab = crmOnly ? "crm" : tab;
  const tasks =
    activeTab === "crm" ? crmTasks : activeTab === "personal" ? personalTasks : allTasks;
  // ALL is a merged read view — disambiguate each row with a source badge.
  const showSource = activeTab === "all";
  // Adding is personal-only (use the MY LIST tab). Editing personal rows is
  // allowed wherever they render (MY LIST and ALL).
  const canAdd = !crmOnly && activeTab === "personal" && !!onCreatePersonal;
  const canEdit = !!onUpdatePersonal;

  // "Needs attention now" = overdue + due-today. Drives both the urgency divider
  // and the per-tab badge counts (urgent-only, not total).
  const isUrgent = (t: TaskItemT) => t.urgency === "overdue" || t.urgency === "today";
  const urgentOf = (ts: TaskItemT[]) => ts.filter(isUrgent).length;
  const crmUrgent = urgentOf(crmTasks);
  const personalUrgent = urgentOf(personalTasks);
  const allUrgent = crmUrgent + personalUrgent;
  const urgentTasks = tasks.filter(isUrgent);
  const futureTasks = tasks.filter((t) => !isUrgent(t));

  const handleSave = (input: PersonalTaskInput) => {
    if (edit?.mode === "edit") {
      onUpdatePersonal?.(edit.id, input);
    } else {
      onCreatePersonal?.(input);
    }
    setEdit(null);
  };

  // One row → either the inline edit form or the source-appropriate task row.
  // Shared by the urgent + future groups so the JSX isn't duplicated.
  const renderRow = (task: TaskItemT) => {
    // QW-3: EVERY task is click-to-expand (empty ones show a "No description"
    // placeholder, so the affordance is consistent across all rows).
    const expanded = expandedId === task.id;
    const onToggleDescription = () =>
      setExpandedId((prev) => (prev === task.id ? null : task.id));
    return canEdit && edit?.mode === "edit" && edit.id === task.id ? (
      <TaskForm
        key={task.id}
        mode="edit"
        initial={{
          subject: task.subject,
          due_date: task.dueDate ?? null,
          priority: task.priority ?? "normal",
        }}
        // Recurrence is read-only in Edge — lock the date field.
        dueReadOnly={task.recurring}
        onSave={handleSave}
        onCancel={() => setEdit(null)}
      />
    ) : task.source === "personal" ? (
      <PersonalTaskRow
        key={task.id}
        task={task}
        editable={canEdit}
        showSource={showSource}
        expanded={expanded}
        onToggleDescription={onToggleDescription}
        onComplete={onCompletePersonal}
        onSnooze={onSnoozePersonal}
        onEdit={() => setEdit({ mode: "edit", id: task.id })}
        // Recurring tasks: the date is read-only in Edge, so no quick
        // reschedule (the worker would 409 anyway).
        onReschedule={
          onReschedulePersonal && !task.recurring
            ? (date) => onReschedulePersonal(task.id, date)
            : undefined
        }
      />
    ) : (
      <CrmTaskRow
        key={task.id}
        task={task}
        showSource={showSource}
        expanded={expanded}
        onToggleDescription={onToggleDescription}
        onComplete={
          onCompleteCrm ? () => setPendingCrm({ kind: "complete", task }) : undefined
        }
        onReschedule={
          onRescheduleCrm
            ? () =>
                setPendingCrm({
                  kind: "reschedule",
                  task,
                  // Default the picker to the current due date so a 1-day move is
                  // a 1-click adjustment. Falls back to today if no due date.
                  date: task.dueDate ?? todayISO(),
                })
            : undefined
        }
        onEdit={
          onEditCrm ? () => setPendingCrm({ kind: "edit", task }) : undefined
        }
      />
    );
  };

  return (
    <>
      <Panel
        className="tasks-rail"
        title="TASKS"
        headerAction={
          canAdd ? (
            <button
              className="add-task-btn"
              style={{ fontSize: 10, padding: "3px 8px" }}
              onClick={() =>
                setEdit((e) => (e?.mode === "new" ? null : { mode: "new" }))
              }
            >
              + ADD
            </button>
          ) : null
        }
        bodyless
      >
        {!crmOnly && (
          <div className="tab-row">
            <button
              className={clsx("tab", tab === "crm" && "active")}
              onClick={() => {
                setTab("crm");
                setEdit(null);
              }}
            >
              CRM <UrgentBadge urgent={crmUrgent} total={crmTasks.length} />
            </button>
            <button
              className={clsx("tab", tab === "personal" && "active")}
              onClick={() => setTab("personal")}
            >
              MY LIST <UrgentBadge urgent={personalUrgent} total={personalTasks.length} />
            </button>
            <button
              className={clsx("tab", tab === "all" && "active")}
              onClick={() => {
                setTab("all");
                setEdit(null);
              }}
            >
              ALL <UrgentBadge urgent={allUrgent} total={crmTasks.length + personalTasks.length} />
            </button>
          </div>
        )}

        {canAdd && edit?.mode === "new" && (
          <TaskForm
            mode="create"
            initial={{ subject: "" }}
            projects={projects}
            onCreateProject={onCreateProject}
            onSave={handleSave}
            onCancel={() => setEdit(null)}
          />
        )}

        <div className="panel-body">
          {tasks.length === 0 ? (
            <div className="tasks-empty">
              {activeTab === "personal"
                ? "Nothing on your list"
                : activeTab === "crm"
                  ? "No open CRM tasks"
                  : "No tasks"}
            </div>
          ) : null}
          {/* "Needs attention now" group (overdue + today). */}
          {urgentTasks.map(renderRow)}
          {/* Divider marks the now → later boundary — only when both sides exist. */}
          {urgentTasks.length > 0 && futureTasks.length > 0 && (
            <div className="task-divider">
              <span className="task-divider-label">UPCOMING</span>
            </div>
          )}
          {/* Future group (everything not overdue/today). */}
          {futureTasks.map(renderRow)}
          {activeTab === "personal" && completedPersonal && (
            <CompletedSection
              completed={completedPersonal}
              mutationSignal={personalVersion}
            />
          )}
        </div>
      </Panel>

      {pendingCrm?.kind === "complete" && (
        <WriteConfirm
          title="Mark complete in Zoho CRM"
          body={
            <>
              <div className="wc-task-subject">{pendingCrm.task.subject}</div>
              {pendingCrm.task.dealName && (
                <div className="wc-task-deal">Linked deal: {pendingCrm.task.dealName}</div>
              )}
            </>
          }
          changes={[
            { label: "Status", from: "Open", to: "Completed" },
          ]}
          confirmLabel="COMPLETE"
          onConfirm={async () => {
            await onCompleteCrm!(pendingCrm.task);
            setPendingCrm(null);
          }}
          onCancel={() => setPendingCrm(null)}
        />
      )}

      {pendingCrm?.kind === "reschedule" && (
        <WriteConfirm
          title="Reschedule in Zoho CRM"
          body={
            <>
              <div className="wc-task-subject">{pendingCrm.task.subject}</div>
              {pendingCrm.task.dealName && (
                <div className="wc-task-deal">Linked deal: {pendingCrm.task.dealName}</div>
              )}
              <label className="wc-date-label">
                New due date
                <input
                  className="wc-date-input"
                  type="date"
                  value={pendingCrm.date}
                  onChange={(e) =>
                    setPendingCrm({ ...pendingCrm, date: e.target.value })
                  }
                  autoFocus
                />
              </label>
            </>
          }
          changes={[
            {
              label: "Due date",
              from: shortDate(pendingCrm.task.dueDate),
              to: shortDate(pendingCrm.date),
            },
          ]}
          confirmLabel="RESCHEDULE"
          onConfirm={async () => {
            await onRescheduleCrm!(pendingCrm.task, pendingCrm.date);
            setPendingCrm(null);
          }}
          onCancel={() => setPendingCrm(null)}
        />
      )}

      {pendingCrm?.kind === "edit" && (
        <CrmTaskEditDialog
          task={pendingCrm.task}
          onSave={async (id, fields) => {
            await onEditCrm!(id, fields);
            setPendingCrm(null);
          }}
          onCancel={() => setPendingCrm(null)}
        />
      )}
    </>
  );
}

function PersonalTaskRow({
  task,
  editable,
  showSource,
  expanded,
  onToggleDescription,
  onComplete,
  onSnooze,
  onEdit,
  onReschedule,
}: {
  task: TaskItemT;
  editable: boolean;
  showSource?: boolean;
  expanded?: boolean;
  onToggleDescription?: () => void;
  onComplete?: (t: TaskItemT) => void;
  onSnooze?: (t: TaskItemT) => void;
  onEdit?: () => void;
  onReschedule?: (date: string) => void;
}) {
  const dateRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    const input = dateRef.current;
    if (!input || !onReschedule) return;
    if (typeof input.showPicker === "function") {
      input.showPicker();
    } else {
      // Older browsers: focus + click as a fallback. Usually opens the
      // picker in Chromium and is harmless elsewhere.
      input.focus();
      input.click();
    }
  };

  return (
    <TaskRowShell
      task={task}
      showSource={showSource}
      expanded={expanded}
      onToggleDescription={onToggleDescription}
      onDueClick={onReschedule ? openPicker : undefined}
      hiddenDateInput={
        onReschedule ? (
          <input
            ref={dateRef}
            type="date"
            // Key on dueDate so the input re-mounts when the task's date
            // changes (so showPicker starts from the new value next time).
            key={task.dueDate ?? "none"}
            defaultValue={task.dueDate ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v) onReschedule(v);
            }}
            className="task-due-picker"
            aria-hidden="true"
            tabIndex={-1}
          />
        ) : undefined
      }
    >
      <button
        className="task-action-btn"
        title="Complete"
        onClick={(e) => {
          e.stopPropagation();
          onComplete?.(task);
        }}
      >
        ✓
      </button>
      <button
        className="task-action-btn"
        title="Snooze 1 day"
        onClick={(e) => {
          e.stopPropagation();
          onSnooze?.(task);
        }}
      >
        ⏱
      </button>
      {editable && (
        <button
          className="task-action-btn"
          title="Edit"
          onClick={(e) => {
            e.stopPropagation();
            onEdit?.();
          }}
        >
          ✎
        </button>
      )}
    </TaskRowShell>
  );
}

function CrmTaskRow({
  task,
  showSource,
  expanded,
  onToggleDescription,
  onComplete,
  onReschedule,
  onEdit,
}: {
  task: TaskItemT;
  showSource?: boolean;
  expanded?: boolean;
  onToggleDescription?: () => void;
  onComplete?: () => void;
  onReschedule?: () => void;
  onEdit?: () => void;
}) {
  return (
    <TaskRowShell
      task={task}
      showSource={showSource}
      expanded={expanded}
      onToggleDescription={onToggleDescription}
    >
      <button
        className="task-action-btn"
        title="Mark complete in Zoho (write-confirm)"
        onClick={(e) => {
          e.stopPropagation();
          onComplete?.();
        }}
        disabled={!onComplete}
      >
        ✓
      </button>
      <button
        className="task-action-btn"
        title="Reschedule in Zoho (write-confirm)"
        onClick={(e) => {
          e.stopPropagation();
          onReschedule?.();
        }}
        disabled={!onReschedule}
      >
        📅
      </button>
      {onEdit && (
        <button
          className="task-action-btn"
          title="Edit in Zoho (subject / due / priority / status / description)"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          ✎
        </button>
      )}
    </TaskRowShell>
  );
}

function TaskRowShell({
  task,
  children,
  showSource,
  expanded,
  onToggleDescription,
  onDueClick,
  hiddenDateInput,
}: {
  task: TaskItemT;
  children: React.ReactNode;
  /** ALL view: render a CRM / MY LIST source badge so the merged list stays legible. */
  showSource?: boolean;
  /** QW-3: when set (task has a description), the subject toggles a description block. */
  expanded?: boolean;
  onToggleDescription?: () => void;
  /** Personal-only: makes the due-date label a quick reschedule trigger. */
  onDueClick?: () => void;
  /** Personal-only: hidden <input type="date"> sibling, anchored for showPicker. */
  hiddenDateInput?: React.ReactNode;
}) {
  const cls = clsx(
    "task-item",
    task.urgency === "overdue" && "overdue",
    task.urgency === "today" && "today",
    task.source === "personal" && "personal",
  );
  const badge =
    task.urgency === "overdue"
      ? "▸ OVERDUE"
      : task.urgency === "today"
        ? "▸ TODAY"
        : null;

  return (
    <div className={cls}>
      {badge && <div className="task-badge">{badge}</div>}
      <div
        className={clsx("task-subject", onToggleDescription && "task-subject-clickable")}
        onClick={
          onToggleDescription
            ? (e) => {
                e.stopPropagation();
                onToggleDescription();
              }
            : undefined
        }
        title={onToggleDescription ? "Click to show/hide description" : undefined}
      >
        {showSource && (
          <span
            className={clsx(
              "task-source-badge",
              task.source === "crm" ? "src-crm" : "src-personal",
            )}
          >
            {task.source === "crm" ? "CRM" : "MY LIST"}
          </span>
        )}
        {task.priority === "high" && <span className="prio-pill prio-hi">HI</span>}
        {task.priority === "low" && <span className="prio-pill prio-lo">LO</span>}
        {task.recurring && (
          <span className="task-recurring" title="Repeats — edit the schedule in Todoist">
            ↻
          </span>
        )}
        {task.subject}
        {onToggleDescription && (
          <span className="task-desc-chevron">{expanded ? " ▾" : " ▸"}</span>
        )}
      </div>
      {task.source === "personal" && task.projectName && (
        <div className="task-project">
          <span className="task-project-dot" />
          {task.projectName}
        </div>
      )}
      {task.dealName && <div className="task-deal">{task.dealName}</div>}
      {onDueClick ? (
        <button
          className="task-due task-due-clickable"
          onClick={(e) => {
            e.stopPropagation();
            onDueClick();
          }}
          title="Click to change due date"
        >
          {task.dueLabel}
        </button>
      ) : (
        <div className="task-due">{task.dueLabel}</div>
      )}
      <div className="task-actions">{children}</div>
      {hiddenDateInput}
      {expanded && (
        <div className="task-description">
          {task.description?.trim() ? (
            task.description
          ) : (
            <span className="task-description-empty">No description</span>
          )}
        </div>
      )}
    </div>
  );
}

function TaskForm({
  mode,
  initial,
  projects,
  onCreateProject,
  dueReadOnly,
  onSave,
  onCancel,
}: {
  mode: "create" | "edit";
  initial: PersonalTaskInput;
  projects?: TodoistProjectLite[];
  onCreateProject?: (name: string) => Promise<TodoistProjectLite | null>;
  /** Edit mode: lock the date field for a recurring task (read-only in Edge). */
  dueReadOnly?: boolean;
  onSave: (input: PersonalTaskInput) => void;
  onCancel: () => void;
}) {
  const [subject, setSubject] = useState(initial.subject);
  const [dueDate, setDueDate] = useState<string>(initial.due_date ?? "");
  const [priority, setPriority] = useState<"high" | "normal" | "low">(
    initial.priority ?? "normal",
  );
  const [projectId, setProjectId] = useState<string>("");
  const [newProject, setNewProject] = useState<string | null>(null); // inline "+ New project" name
  const [creating, setCreating] = useState(false);

  const submit = () => {
    const trimmed = subject.trim();
    if (!trimmed) return;
    onSave({
      subject: trimmed,
      due_date: dueDate ? dueDate : null,
      priority,
      // Project is picked at create time only (move-on-edit is a later step).
      project_id: mode === "create" && projectId ? projectId : undefined,
    });
  };

  const submitNewProject = async () => {
    const name = (newProject ?? "").trim();
    if (!name || !onCreateProject) return;
    setCreating(true);
    const p = await onCreateProject(name);
    setCreating(false);
    if (p) {
      setProjectId(p.id);
      setNewProject(null);
    }
  };

  return (
    <div className="task-form">
      <input
        className="task-form-subject"
        type="text"
        placeholder="What needs doing?"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape") onCancel();
        }}
        autoFocus
      />
      <div className="task-form-row">
        <input
          className="task-form-date"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          disabled={dueReadOnly}
          title={dueReadOnly ? "Recurring task — change its date in Todoist" : undefined}
          aria-label="Due date"
        />
        <select
          className="task-form-priority"
          value={priority}
          onChange={(e) =>
            setPriority(e.target.value as "high" | "normal" | "low")
          }
          aria-label="Priority"
        >
          <option value="high">HIGH</option>
          <option value="normal">NORMAL</option>
          <option value="low">LOW</option>
        </select>
        <button className="task-form-cancel" onClick={onCancel} title="Cancel">
          ×
        </button>
        <button className="task-form-save" onClick={submit} title="Save">
          SAVE
        </button>
      </div>
      {mode === "create" && (
        <div className="task-form-row">
          {newProject === null ? (
            <select
              className="task-form-project"
              value={projectId}
              onChange={(e) => {
                if (e.target.value === "__new__") setNewProject("");
                else setProjectId(e.target.value);
              }}
              aria-label="Project"
            >
              <option value="">Inbox</option>
              {(projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
              {onCreateProject && <option value="__new__">+ New project…</option>}
            </select>
          ) : (
            <>
              <input
                className="task-form-newproject"
                type="text"
                placeholder="New project name"
                value={newProject}
                onChange={(e) => setNewProject(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void submitNewProject();
                  if (e.key === "Escape") setNewProject(null);
                }}
                autoFocus
              />
              <button
                className="task-form-save"
                onClick={() => void submitNewProject()}
                disabled={creating || !newProject.trim()}
                title="Create project"
              >
                {creating ? "…" : "ADD"}
              </button>
              <button
                className="task-form-cancel"
                onClick={() => setNewProject(null)}
                title="Cancel"
              >
                ×
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CompletedSection({
  completed,
  mutationSignal,
}: {
  completed: CompletedTasksState;
  mutationSignal?: number;
}) {
  const { tasks, loading, error, load } = completed;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  // Track whether we've ever opened so we don't hammer the API on initial render.
  const openedRef = useRef(false);

  // Effect fires on open, search-change, OR when an active task mutates
  // (mutationSignal increment). The 250ms debounce coalesces typing AND
  // batches a quick complete-then-look scenario.
  useEffect(() => {
    if (!open) return;
    openedRef.current = true;
    const handle = setTimeout(() => load(q || undefined), 250);
    return () => clearTimeout(handle);
  }, [open, q, load, mutationSignal]);

  if (!open) {
    return (
      <button
        className="completed-toggle"
        onClick={() => setOpen(true)}
      >
        ▸ SHOW COMPLETED
      </button>
    );
  }

  return (
    <div className="completed-section">
      <div className="completed-section-header">
        <button
          className="completed-toggle completed-toggle-open"
          onClick={() => {
            setOpen(false);
            setQ("");
          }}
        >
          ▾ COMPLETED ({completed.tasks.length})
        </button>
        <input
          className="completed-search"
          type="search"
          placeholder="search…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {loading && openedRef.current ? (
        <div className="tasks-empty">Loading…</div>
      ) : error ? (
        <div className="tasks-empty">⚠ {error}</div>
      ) : tasks.length === 0 ? (
        <div className="tasks-empty">
          {q ? "No matches" : "No completed tasks yet"}
        </div>
      ) : (
        tasks.map((task) => <CompletedTaskRow key={task.id} task={task} />)
      )}
    </div>
  );
}

function CompletedTaskRow({ task }: { task: TaskItemT }) {
  const completedAt = task.completedAt
    ? new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles",
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(new Date(task.completedAt))
    : "—";
  return (
    <div className="task-item task-item-completed">
      <div className="task-subject">
        {task.priority === "high" && <span className="prio-pill prio-hi">HI</span>}
        {task.priority === "low" && <span className="prio-pill prio-lo">LO</span>}
        {task.subject}
      </div>
      <div className="task-due task-due-completed">✓ {completedAt}</div>
    </div>
  );
}

/**
 * Per-tab badge showing the "needs attention now" count (overdue + due-today)
 * — NOT the total. >0 → an alert-styled number. 0 → a calm ✓ that reads "you're
 * clear on urgent" (with the upcoming count in the tooltip), so an all-future
 * tab never looks empty.
 */
function UrgentBadge({ urgent, total }: { urgent: number; total: number }) {
  if (urgent > 0) {
    return (
      <span className="tab-count tab-count-urgent" title={`${urgent} need attention now`}>
        {urgent}
      </span>
    );
  }
  return (
    <span
      className="tab-count tab-count-clear"
      title={total > 0 ? `Nothing urgent today · ${total} upcoming` : "Nothing here"}
    >
      ✓
    </span>
  );
}

/** "May 15" or "(no date)" — for write-confirm field summaries. */
function shortDate(iso?: string | null): string {
  if (!iso) return "(no date)";
  const d = new Date(`${iso}T12:00:00Z`);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "numeric",
  }).format(d);
}

/** "YYYY-MM-DD" in PT — for the date input default value. */
function todayISO(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
  }).format(new Date());
}
